import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 1-2 (Emitido/Detalle) de una OT de Traspaso de Equipos Activos (tipo_ot=10):
// espejo de index.php + guarda.php de traspaso_activos/ — un solo paso, sin pantalla de
// "Finalizar" separada (guarda.php no tiene un concluyendo.php aparte: seleccionar
// equipo + sucursal destino + observaciones resuelve la OT de una vez). DISTINTO de
// "Centro de Equipos > Traspaso": acá se mueve la sucursal que trackea un equipo que YA
// ESTÁ ACTIVO con este cliente, no equipo sin cliente.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
// Input nativo (Box component="input") en vez de TextField variant="standard" para
// la celda de observación sin label — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontFamily: "inherit",
  fontSize: 10.5,
  padding: "2px 2px",
  border: "none",
  borderBottom: "1px solid",
  borderColor: "divider",
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled" },
};

const PAGE_SIZE = 15;
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Modem: "Módems", Chip: "Chips" };
const familiaDe = (t) => ({ Radio: "RADIO", Bateria: "BATERIA", Modem: "MODEM", Chip: "CHIP" }[t] || "VARIOS");

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Modem") return "/api/equipos-modem";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

// Misma lista que ya usa Centro de Equipos > Traspaso (TraspasoEquipos.jsx) — más
// completa que las 3 opciones fijas del PHP viejo (La Paz/Cochabamba/Santa Cruz) y es la
// lista vigente de sucursales reales.
const SUCURSALES_DESTINO = [
  "La Paz",
  "Cochabamba",
  "Santa Cruz",
  "Oruro",
  "Chuquisaca",
  "Pando",
  "Beni",
  "Tarija",
  "Potosi",
  "Yacuiba",
  "La Paz(El Alto)",
  "Uyuni",
];

const TraspasoActivosOT = ({ cod_ot, cod_servicio_cliente, servicio, esTKR, esAVL, onEnviado }) => {
  const { user } = useAuth();
  const [tiposOtros, setTiposOtros] = useState([]);
  const tipos = esTKR ? ["Radio", "Bateria"] : esAVL ? ["Modem", "Chip"] : tiposOtros;
  const [tipo, setTipo] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");
  const [equipoDialog, setEquipoDialog] = useState(null);

  // { [key]: { tipoEquipo, identificador, observacion } }
  const [seleccion, setSeleccion] = useState({});
  const [sucursalDestino, setSucursalDestino] = useState("");
  const [observacionGeneral, setObservacionGeneral] = useState("");
  const [guardando, setGuardando] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (esTKR || esAVL) return;
    axios.get("/api/equipos-varios/tipos", { params: { servicio } })
      .then((res) => setTiposOtros(res.data || []))
      .catch(() => setTiposOtros([]));
  }, [esTKR, esAVL, servicio]);

  useEffect(() => {
    if (tipos.length && !tipo) setTipo(tipos[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipos.join("|")]);

  // Equipo Activo de este cliente en CUALQUIER ciudad (sin filtro de ciudad, a
  // propósito: elegir a dónde se traspasa ES el sentido de esta pantalla).
  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    if (!tipo) return;
    setLoadingEquipos(true);
    setError("");
    const params = { estado: "Activo", cod_servicio: cod_servicio_cliente, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE };
    // /api/equipos-varios necesita `tipo` para distinguir entre los sub-tipos dinámicos
    // (Accesorio POC, CLIP ENRICO, etc.) — los demás endpoints ya son específicos por
    // familia (equipos-radio, equipos-modem, ...) y no lo usan.
    if (!["Radio", "Bateria", "Modem", "Chip"].includes(tipo)) params.tipo = tipo;
    axios
      .get(endpointDeTipo(tipo), { params })
      .then((res) => {
        const data = res.data || {};
        const items = data.items || data.rows || [];
        const identificadorDe = (r) => r.serie ?? r.num_sim ?? r.numero;
        setItems(items.map((r) => ({
          key: `${tipo.toLowerCase()}-${r.ID ?? r.ID_A ?? identificadorDe(r)}`,
          tipoEquipo: tipo,
          identificador: identificadorDe(r),
          modelo: r.modelo,
          ciudad: r.ciudad,
        })));
        setTotal(Number(data.total ?? 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => {
        console.error("Error al cargar equipo Activo", err);
        setError("No se pudo cargar el equipo disponible.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, cod_servicio_cliente]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = (row) => {
    setSeleccion((prev) => {
      if (prev[row.key]) {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      }
      return { ...prev, [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador, observacion: "" } };
    });
  };

  const setObservacionItem = (key, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], observacion: valor } }));
  };

  const seleccionados = Object.values(seleccion);

  const handleGuardar = async () => {
    if (!seleccionados.length || !sucursalDestino) return;
    if (!window.confirm(`¿Traspasar ${seleccionados.length} equipo(s) a ${sucursalDestino}?`)) return;
    setGuardando(true);
    setError("");
    try {
      const payload = {
        cod_ot,
        items: seleccionados.map((s) => ({
          familia: familiaDe(s.tipoEquipo),
          identificador: s.identificador,
          observacion: s.observacion || "",
        })),
        sucursalOrigen: user?.sucursal || "",
        sucursalDestino,
        observacionGeneral,
      };
      const { data } = await axios.post("/api/ot-traspaso-activos/guardar", payload);
      if (onEnviado) onEnviado(data);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar el traspaso.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {error && <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>{error}</MuiAlert>}

      {tipos.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <ToggleButtonGroup size="small" exclusive color="primary" value={tipo} onChange={(e, nuevo) => nuevo && setTipo(nuevo)} sx={{ height: 30 }}>
            {tipos.map((t) => (
              <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                {TIPO_LABEL[t] || t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
        <TextField
          size="small"
          label="Buscar"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
          sx={{ minWidth: 160, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos Activos del Cliente
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Ciudad Actual</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEquipos ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}><CircularProgress size={18} /></TableCell></TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Este cliente no tiene {(TIPO_LABEL[tipo] || tipo || "").toLowerCase()} activos.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const datos = seleccion[row.key];
                const checked = Boolean(datos);
                return (
                  <TableRow key={row.key} hover selected={checked}>
                    <TableCell>
                      <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                    <TableCell>{row.ciudad}</TableCell>
                    <TableCell>
                      <Box
                        component="input"
                        disabled={!checked}
                        value={datos?.observacion || ""}
                        onChange={(e) => setObservacionItem(row.key, e.target.value)}
                        sx={{ ...inputCompactoSx, width: "100%" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingEquipos || page <= 1}
          onClick={() => cargarPagina(page - 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Anterior
        </Button>
        <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingEquipos || page >= totalPages}
          onClick={() => cargarPagina(page + 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Siguiente
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Select
          size="small"
          displayEmpty
          value={sucursalDestino}
          onChange={(e) => setSucursalDestino(e.target.value)}
          sx={{ minWidth: 200, fontSize: 11.5, height: 30 }}
        >
          <MenuItem value="" disabled sx={{ fontSize: 11 }}><em>Sucursal destino</em></MenuItem>
          {SUCURSALES_DESTINO.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
          ))}
        </Select>
      </Stack>

      <TextField
        label="Observaciones Generales"
        fullWidth
        multiline
        rows={3}
        value={observacionGeneral}
        onChange={(e) => setObservacionGeneral(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length || !sucursalDestino || guardando}
          onClick={handleGuardar}
          sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
        >
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar Traspaso"}
        </Button>
      </Box>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default TraspasoActivosOT;
